import React, { FunctionComponent, useEffect, useState } from "react";
import { Card, CardCover } from "@mui/joy";
import "./BulkUpload.scss";
import images from "../../../assets/images";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import { AllowedPath } from "../AdminBulkUpload";
import { getDisplayType, getFileType } from "./Helper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { UPLOAD_LIBRARY_IMAGE_ADMIN } from "../../ImageMaterial/api/ImageMaterialQuery";
import ToasterWithMessage from "../../../components/toaster";
import { GET_PRESIGNED_URL } from "../../../components/Popups/api/FileUploadQuery";
import { CLASSROOM_S3_PATH, messages, patterns } from "../../../utils/constants";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import UploadResultModal from "./UploadResultModal";
import { UPLOAD_SYNDICATED_VIDEOS_ADMIN } from "../../SyndicatedVideos/api/SyndicatedVideosQuery";
import { UPLOAD_PODCAST_ADMIN } from "../../PodCast/api/PodCastQueries";
import { UPLOAD_MATERIAL_ADMIN } from "../../TeacherTeachingMaterial/api/TeacherTeachingMaterialQuery";
import { UPLOAD_QUESTIONS_BANK_ADMIN } from "../../QuestionBank/api/QuestionsBankQuery";
import ConfirmationPopup from "../../../components/ConfirmationPopup/ConfirmationPopup";

interface BulkUploadProps {
  selectedPath: AllowedPath;
  selectedClassIds: string[];
  selectedSubject: string;
  setHasSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  ErrorInFields: string | null;
  videoCategoryId?: string | null;
  descriptionValue?: string | null;
  authorName?: string | null;
}

export interface UploadResult {
  filename: string;
  duplicates: number;
  classrooms_failed: { c_id: string; c_name: string }[];
  classrooms_updated: { c_id: string; c_name: string }[];
  skipped_no_subject: number;
  classrooms_considered: { c_id: string; c_name: string }[];
  classrooms_failed_count: number;
  classrooms_updated_count: number;
  classrooms_total_considered: number;
}

const BulkUpload: FunctionComponent<BulkUploadProps> = ({
  selectedPath,
  selectedClassIds,
  selectedSubject,
  setHasSubmitted,
  ErrorInFields,
  videoCategoryId,
  descriptionValue,
  authorName,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const {
    podcast,
    pdfImg,
    csvImg,
    docsImg,
    excelPreview,
    powerpoint,
    epub,
    BulkUplodIcon,
    ShareGif
  } = images;
  const { error, success } = ToasterWithMessage();
  const [loading, setLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
  const [isShareMaterial, setIsShareMaterial] = useState<boolean | undefined>(undefined);
  let folderName: string = "";
  console.log(videoCategoryId, "videoid");

  const handleFiles = (uploadedFiles: FileList | null) => {
    if (!uploadedFiles) return;

    const newFiles = Array.from(uploadedFiles).filter((file) => {
      // Check file name validity first
      const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      // 🔹 Run validations using your existing patterns & messages
      if (!patterns.fileNameRegex.test(fileNameWithoutExtension)) {
        error(messages.inBulkUploadfileName);
        return false;
      }

      if (patterns.consecutiveSpecialCharsRegex.test(fileNameWithoutExtension)) {
        error(messages.consecutiveSpecialChars);
        return false;
      }

      if (!patterns.filenameAtleast1Alpha.test(fileNameWithoutExtension)) {
        error(messages.filenameAtlease1Alpha);
        return false;
      }

      let maxAllowedSize = 0;

      if (selectedPath === "Images") {
        maxAllowedSize = 5 * 1024 * 1024; // 5MB
      } else if (
        selectedPath === "E-books (Examination Guide)" ||
        selectedPath === "E-books (Learning Materials)" ||
        selectedPath === "E-books (Question Bank)"
      ) {
        maxAllowedSize = 25 * 1024 * 1024; // 25MB
      } else if (selectedPath?.includes("Podcast")) {
        maxAllowedSize = 100 * 1024 * 1024; // 100MB
      } else if (selectedPath?.includes("Videos")) {
        maxAllowedSize = 500 * 1024 * 1024; // 500MB
      }

      if (file.size > maxAllowedSize) {
        error(
          `${file.name} exceeds the max allowed size of ${
            maxAllowedSize / (1024 * 1024)
          } MB`
        );
        return false;
      }

      return true;
    });

    if (newFiles.length > 0) {
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const bucketname = process.env.REACT_APP_MEDIA_BUCKET_NAME;
  const region = process.env.REACT_APP_REGION;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
    e.dataTransfer.clearData();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleRemove = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setFiles([]);
  }, [selectedPath]);

  // utils/filePreview.ts
  const getFilePreview = (file: File): string | "video" | "audio" => {
    if (file.type.startsWith("video/")) {
      return "video";
    }
    if (file.type.startsWith("audio/")) {
      return podcast;
    }
    switch (file.type) {
      case "application/pdf":
        return pdfImg;
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        return docsImg;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.ms-excel":
        return excelPreview;
      case "application/vnd.ms-powerpoint":
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        return powerpoint;
      case "application/epub+zip":
        return epub;
      default:
        if (file.type.startsWith("text/")) return csvImg;
        if (file.type.startsWith("image/")) return URL.createObjectURL(file);
        return pdfImg;
    }
  };

  const [getPreSignedUrl] = useLazyQuery(GET_PRESIGNED_URL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {},
    onError: (err) => {
      // error("Something went wrong!")
    },
  });

  const [uploadImageMaterialAdmin] = useMutation(UPLOAD_LIBRARY_IMAGE_ADMIN, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log(data);
    },
    onError: (err) => {
      error(err?.message);
    },
  });

  const [uploadSyndicatedvideoAdmin] = useMutation(
    UPLOAD_SYNDICATED_VIDEOS_ADMIN,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        console.log(data);
      },
      onError: (err) => {
        error(err?.message);
      },
    }
  );

  const [uploadPodcastAdmin] = useMutation(UPLOAD_PODCAST_ADMIN, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log(data);
    },
    onError: (err) => {
      error(err.message);
    },
  });

  const [uploadTeachingMaterialAdmin] = useMutation(UPLOAD_MATERIAL_ADMIN, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log(data);
    },
    onError: (err) => {
      error(err?.message);
    },
  });

  const [uploadQuestionBankAdmin] = useMutation(UPLOAD_QUESTIONS_BANK_ADMIN, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log(data);
    },
    onError: (err) => {
      error(err.message);
    },
  });

  const handleBulkUplad = async () => {
    setHasSubmitted(true);
    if (files.length === 0 || selectedClassIds.length === 0 || ErrorInFields)
      return;
    setLoading(true);
    try {
      if(selectedPath === "Images") {
        folderName = "images";
      } else {
        folderName = "materials";
      }

      // Prepare all files for upload in parallel
      const uploadPromises = files.map(async (file) => {
        const sanitizedFileName = file.name.replace(/ /g, "_");
        const extension = file.name.slice(file.name.lastIndexOf("."));
        const newFileName = `${CLASSROOM_S3_PATH}${folderName}/${sanitizedFileName}-${Date.now()}${extension}`;

        // 1️⃣ Get presigned URL
        const { data } = await getPreSignedUrl({
          variables: { input: { key: newFileName } },
        });
        const { preSignedUrl } = data.getUploadPresignedUrl;

        // 2️⃣ Upload image to S3
        await axios.put(preSignedUrl, file, {
          headers: { "Content-Type": file.type },
        });

        // 3️⃣ Extract S3 key
        const s3Key = preSignedUrl
          .split("?")[0]
          .split(`https://${bucketname}.s3.${region}.amazonaws.com/`)[1];
        const fileNameWithoutExtension = file.name
          .split(".")
          .slice(0, -1)
          .join(".");
        // Return file info for mutation
        return {
          filename: fileNameWithoutExtension,
          filepath: s3Key,
          filesize: file.size,
          isClassroom: isShareMaterial,
          // coverImagePath: null,

          ...((selectedPath === "E-books (Learning Materials)" ||
            selectedPath === "E-books (Examination Guide)") && {
            filetype: getFileType(file.type),
          }),

          ...(descriptionValue && { fileDescription: descriptionValue }),

          ...(videoCategoryId?.trim() && {
            category_id: videoCategoryId.trim(),
          }),

          ...(selectedPath === "E-books (Examination Guide)" && {
            authorName: authorName,
          }),
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      let response;
      let results;

      if (selectedPath === "Images") {
        response = await uploadImageMaterialAdmin({
          variables: {
            files: uploadedFiles,
            classroomIds: selectedClassIds,
            subjectId: selectedSubject,
          },
        });
        results = response?.data.uploadMylibraryImagesBySchoolAdmin?.result;
      } else if (selectedPath === "Syndicated Videos") {
        response = await uploadSyndicatedvideoAdmin({
          variables: {
            files: uploadedFiles,
            classroomIds: selectedClassIds,
            subjectId: selectedSubject,
          },
        });
        results =
          response?.data.uploadMylibrarySyndicatedVideosBySchoolAdmin?.result;
      } else if (selectedPath === "Podcast") {
        response = await uploadPodcastAdmin({
          variables: {
            files: uploadedFiles,
            classroomIds: selectedClassIds,
            subjectId: selectedSubject,
          },
        });
        results = response?.data.uploadMylibraryPodcastsBySchoolAdmin?.result;
      } else if (selectedPath === "E-books (Learning Materials)") {
        response = await uploadTeachingMaterialAdmin({
          variables: {
            files: uploadedFiles,
            classroomIds: selectedClassIds,
            subjectId: selectedSubject,
            typeName: "Materials",
          },
        });
        results = response?.data.uploadMylibraryEbooksBySchoolAdmin?.result;
      } else if (selectedPath === "E-books (Examination Guide)") {
        response = await uploadTeachingMaterialAdmin({
          variables: {
            files: uploadedFiles,
            classroomIds: selectedClassIds,
            subjectId: selectedSubject,
            typeName: "Examination Guide",
          },
        });
        results = response?.data.uploadMylibraryEbooksBySchoolAdmin?.result;
      } else if (selectedPath === "E-books (Question Bank)") {
        response = await uploadQuestionBankAdmin({
          variables: {
            files: uploadedFiles,
            classroomIds: selectedClassIds,
            subjectId: selectedSubject,
          },
        });
        results = response?.data.uploadQuestionBanksByAdmin?.result;
      }

      if (results) {
        setUploadResults(results);

        let hasFailures;
        if(selectedPath === "E-books (Question Bank)") {
          hasFailures = results.some(
            (res: { classroomsFailed: any[] }) =>
              res.classroomsFailed.length > 0
          );
        }else {
          hasFailures = results.some(
            (res: { classrooms_failed: any[] }) =>
              res.classrooms_failed.length > 0
          );
        }
        

        if (hasFailures) {
          setIsModalOpen(true);
        } else {
          success("File uploaded Successfully!");
        }
      }

      // 5️⃣ Clear files and show success
      setFiles([]);
      console.log("All images uploaded successfully!");
    } catch (err: any) {
      console.error("Bulk upload failed:", err);
      error(err?.message);
    } finally {
      setLoading(false); // stop loading
      setIsShareMaterial(undefined);
    }
  };

  const onYesButtonClick = () => {
    setIsShareMaterial(true);
    setOpenConfirmationPopup(false);
  }

  const onNoButtonClick = () => {
    setIsShareMaterial(false);
    setOpenConfirmationPopup(false);
  }

  useEffect(() => {
    if(isShareMaterial !== undefined)
    handleBulkUplad();
  }, [isShareMaterial]);

  return (
    <>
      <div className="bulk-upload">
        <div
          className={`dropzone ${dragActive ? "active" : ""} ${
            files.length > 0 ? "filesupload" : ""
          } `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {/* Previews inside CardCover */}
          {files.length > 0 && (
            <div className="card-preview-wrapper">
              {files.map((file, index) => {
                const url = URL.createObjectURL(file);
                return (
                  <Card key={index} className="preview-card">
                    <CardCover>
                      {file.type.startsWith("video/") ? (
                        <video autoPlay loop muted>
                          <source src={url} />
                        </video>
                      ) : (
                        <img
                          className="previewImage"
                          src={getFilePreview(file)}
                          alt={file.name}
                        />
                      )}
                    </CardCover>

                    <button
                      className="remove-btn"
                      onClick={() => handleRemove(index)}
                    >
                      ✖
                    </button>
                  </Card>
                );
              })}
            </div>
          )}

          {/* File input */}
          {files.length <= 0 && (
            <div className="upload-icon">
              <img src={BulkUplodIcon} alt="" className="upload-img" />
            </div>
          )}

          <input
            type="file"
            // multiple
            accept={
              selectedPath === "Images"
                ? "image/*"
                : selectedPath?.includes("Videos")
                ? "video/*"
                : selectedPath === "Podcast"
                ? "audio/*"
                : selectedPath === "E-books (Learning Materials)"
                ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.epub"
                : selectedPath === "E-books (Question Bank)"
                ? ".csv"
                : selectedPath === "E-books (Examination Guide)"
                ? ".pdf"
                : ".pdf,.doc,.docx,.txt" // fallback
            }
            onChange={handleChange}
            id="fileUpload"
            hidden
          />

          <label htmlFor="fileUpload" className="upload-label">
            Drag & drop{" "}
            <span style={{ color: "#0B74BB" }}>
              {getDisplayType(selectedPath)}
            </span>{" "}
            Here
          </label>
          <span className="browse">
            or{" "}
            <label className="materials" htmlFor="fileUpload">
              browse files
            </label>{" "}
            on your computer
          </span>
        </div>
      </div>
      <div className="upload-button-bulk">
        <ButtonStyle
          deleteBtn={
            loading ? <CircularProgress size={20} color="inherit" /> : undefined
          }
          text="Upload"
          disableButton={
            selectedClassIds.length <= 0 ||
            files.length <= 0 ||
            loading ||
            !!ErrorInFields ||
            (selectedPath === "Syndicated Videos" && !videoCategoryId) ||
            (selectedPath === "Podcast" && !descriptionValue) ||
            (selectedPath === "E-books (Examination Guide)" && !authorName)
          }
          width="100%"
          onClick={() => setOpenConfirmationPopup(true)}
        />
      </div>
      <UploadResultModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        results={uploadResults}
        selectedPath={selectedPath}
      />
      <ConfirmationPopup
          open={openConfirmationPopup}
          message={messages.multipleShareClassesMessage}
          gifData={ ShareGif }
          yesButtonFunctionality={onYesButtonClick}
          noButtonFunctionality={onNoButtonClick}
          messageWidth={'210px'}
      />
    </>
  );
};

export default BulkUpload;
